// App Insight interne (CCT-193, Phase 2) — beacon front de l'app (React Native + RN Web).
//
// Bufferise des événements d'usage et les FLUSH par lot vers `POST /insights/events`
// (endpoint NON authentifié, borné côté serveur). Transport best-effort :
//   - RN Web : navigator.sendBeacon si dispo, sinon fetch(..., { keepalive: true }).
//   - Natif  : fetch simple (sendBeacon/keepalive absents ; l'option est ignorée).
// Flush : sur timer (~5 s), quand le buffer atteint FLUSH_AT, et sur bascule en
// arrière-plan (web: visibilitychange/pagehide ; natif: AppState 'change').
//
// CAVIARDAGE : meta = métadonnées STRUCTURELLES uniquement (jamais de valeur de
// champ, mot de passe ou token). Non bloquant : aucune exception n'est propagée.

import { AppState, Platform } from 'react-native';

import { getEntraConfig } from '../auth/entraConfig';
import { getSessionId, hydrateSessionId, newSessionId, nextSeq, markClientAction, parentSeq } from './session';
import { grouperParSession, ackKey, sidDe } from './batching';

export interface InsightEvent {
  type: string;
  path?: string;
  action?: string;
  meta?: Record<string, unknown>;
  /** Rang dans la session — cf. `track()`. Posé ici, jamais par l'appelant. */
  seq?: number;
  /** Instant de l'ACTION, en ISO. Indispensable dès lors que l'envoi peut être différé de
   *  plusieurs heures (hors ligne) : sans lui, le serveur horodaterait à la RÉCEPTION et un
   *  clic d'hier apparaîtrait à l'heure de la reconnexion. Posé par `track()`. */
  ts?: string;
  /** Session d'ORIGINE, posée à l'empilement (CCT-261). Marqueur LOCAL : retiré avant envoi. */
  _sid?: string;
}

const FLUSH_INTERVAL_MS = 5000;
const FLUSH_AT = 20;
const MAX_BATCH = 50; // aligne le rate-limit backend
const STACK_MAX = 600;

// --- PERSISTANCE DU TAMPON (CCT-253) ----------------------------------------
// Le tampon vivait en mémoire et le lot était RETIRÉ AVANT l'envoi (`splice`), l'échec étant
// avalé sans rien remettre. Hors ligne, en tunnel, backend arrêté, onglet fermé pendant un
// envoi : les actions étaient perdues EN SILENCE. Or c'est précisément l'usage hors ligne
// qu'on veut tracer — et une donnée perdue ne se rattrape pas, contrairement à un affichage.
const LS_QUEUE = 'cc_insight_queue';

// Plafond du tampon persisté. Sans lui, un backend durablement absent remplirait le stockage
// du navigateur (quota ~5 Mo) et finirait par faire échouer TOUTE écriture locale, y compris
// l'identifiant de session. On jette alors les PLUS ANCIENS : en diagnostic, le récent vaut
// plus que l'ancien.
const MAX_STORED = 500;

// Âge au-delà duquel un événement en attente est abandonné. Sans borne, une reconnexion
// après une semaine réinjecterait des événements dans des sessions closes depuis longtemps
// et fausserait leurs timelines. 24 h couvre largement une coupure réelle.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

let _buffer: InsightEvent[] = [];
let _timer: ReturnType<typeof setInterval> | null = null;
let _started = false;

/** Retire ce qui est trop vieux et borne la taille. Exporté pour le test. */
export function prune(events: InsightEvent[], now: number): InsightEvent[] {
  const vivants = events.filter((e) => {
    if (!e || !e.ts) return true;           // sans horodatage : on garde, on ne devine pas
    const t = Date.parse(e.ts);
    if (!Number.isFinite(t)) return true;   // illisible : on garde plutôt que de jeter
    return now - t <= MAX_AGE_MS;
  });
  // On coupe par la TÊTE : les plus anciens partent en premier.
  return vivants.length > MAX_STORED ? vivants.slice(vivants.length - MAX_STORED) : vivants;
}

function persist(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    if (_buffer.length === 0) localStorage.removeItem(LS_QUEUE);
    else localStorage.setItem(LS_QUEUE, JSON.stringify(_buffer));
  } catch {
    /* quota plein ou stockage indisponible : le tampon mémoire reste, on n'échoue pas */
  }
}

/** Recharge le tampon persisté au démarrage. Les actions faites hors ligne repartent ainsi
 *  au chargement suivant, au lieu de disparaître avec l'onglet — mais SOUS LEUR SESSION
 *  D'ORIGINE (CCT-261), pas sous la session courante.
 *
 *  ⚠️ Un événement sans `_sid` est ABANDONNÉ : il vient d'un tampon écrit avant ce correctif,
 *  on ne sait plus à quelle session il appartenait, et le poster sous la session courante lui
 *  inventerait une cause. Perdre une ligne de diagnostic est moins grave que d'en fabriquer
 *  une fausse. */
export function restore(): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(LS_QUEUE);
    if (!raw) return;
    const stockes = JSON.parse(raw);
    if (!Array.isArray(stockes)) return;
    const attribuables = stockes.filter((e: InsightEvent) => sidDe(e));
    _buffer = prune([...attribuables, ..._buffer], Date.now());
    persist();
  } catch {
    // Contenu illisible : on repart propre plutôt que de boucler sur une donnée corrompue.
    try { localStorage.removeItem(LS_QUEUE); } catch { /* ignore */ }
  }
}

function endpoint(): string {
  return `${getEntraConfig().apiBaseUrl.replace(/\/$/, '')}/insights/events`;
}

function trunc(v: unknown, n = STACK_MAX): string | undefined {
  if (typeof v !== 'string') return undefined;
  return v.length > n ? v.slice(0, n) : v;
}

/** Empile un événement (best-effort). Déclenche un flush si le buffer est plein.
 *
 * LE NUMÉRO DE SÉQUENCE EST POSÉ ICI, PAS AU FLUSH (CCT-240). Les événements sont mis en
 * tampon et envoyés jusqu'à 5 s plus tard, par lots : au flush, l'ordre d'origine est déjà
 * perdu et l'horodatage d'ingestion ne le retrouve pas. `track()` est le seul instant où
 * l'on sait VRAIMENT quand l'action a eu lieu.
 *
 * C'est le MÊME compteur que celui de l'en-tête `X-CC-Session-Sequence` des appels API (cf.
 * `api/client.ts`) — délibérément. Un clic et la requête qu'il déclenche reçoivent ainsi
 * des rangs CONSÉCUTIFS dans un ordre total unique, ce qui permet à la timeline du BO de
 * remettre l'appel serveur et son SQL DERRIÈRE le clic qui les a causés. Deux compteurs
 * séparés auraient donné deux échelles incomparables.
 */

/**
 * Pile d'appels JavaScript menant ici — MÊME OBJET que `meta.pile` côté serveur (CCT-274).
 *
 * On lit une timeline pour savoir PAR OÙ, pas seulement quoi. Côté serveur, chaque requête
 * SQL porte son chemin de code ; côté client, un appel réseau n'en portait aucun : on voyait
 * qu'un `GET /missions` était parti, jamais depuis quel écran ni quel gestionnaire.
 *
 * ⚠️ FILTRÉE ET BORNÉE, pour les mêmes raisons que côté serveur : sans filtre la pile est
 * noyée sous les cadres du bundler, de React et des polyfills — illisible pour un humain,
 * coûteuse à stocker, et sans valeur puisque ce n'est pas leur code qu'on diagnostique.
 *
 * ⚠️ L'ORIGINE EST RETIRÉE de chaque ligne : elle est identique partout, et la garder
 * multiplierait la taille du champ par trois pour zéro information.
 */
const LINE_BREAKS = /\r?\n/;
// Cadres à écarter : bundler, React, polyfills, et notre propre instrumentation.
const CADRES_A_IGNORER = /node_modules|\/insights\/|\/api\/client|<anonymous>/;

export function pileJs(profondeurMax = 8): string[] {
  try {
    const brut = new Error().stack;
    if (!brut) return [];
    const origine = (typeof location !== 'undefined' && location?.origin) || '';
    return brut
      .split(LINE_BREAKS)
      .slice(1)                                   // la ligne 0 est « Error », pas un cadre
      .map((l) => l.trim().replace(/^at\s+/, ''))
      .map((l) => (origine ? l.split(origine).join('') : l))
      // Le suffixe anti-cache du bundler (`?t=1785710964645`) change a chaque rechargement :
      // il rendrait deux piles identiques textuellement differentes, ce qui casse tout
      // regroupement et gonfle le champ pour zero information.
      .map((l) => l.replace(/[?][^\s):]*/g, ""))
      .filter((l) => l && !CADRES_A_IGNORER.test(l))
      .slice(0, profondeurMax);
  } catch {
    return [];                                    // l'observabilité ne casse jamais un appel
  }
}


/**
 * Enregistre un APPEL RÉSEAU émis par le client (CCT-274), avec sa pile et sa latence PERÇUE.
 *
 * Deux informations que rien ne portait :
 *   * la PILE — d'où part l'appel. Côté serveur chaque requête SQL a son chemin de code ;
 *     côté client, on voyait qu'un `GET` était parti, jamais depuis quel écran ni quel
 *     gestionnaire.
 *   * la LATENCE PERÇUE — l'écart entre l'envoi et la réception, vu du poste. L'appel API
 *     serveur porte son propre temps de TRAITEMENT ; la différence entre les deux EST le
 *     coût réseau, invisible jusqu'ici. Un appel de 12 ms côté serveur et 900 ms côté client
 *     ne se diagnostique pas au même endroit.
 *
 * ⚠️ LE RANG EST PRIS À L'ENVOI, pas à la réception : c'est lui qu'on annonce en en-tête, et
 * l'appel serveur le désignera comme cause. L'évènement, lui, n'est empilé qu'à la fin —
 * quand la durée et le statut sont connus.
 *
 * ⚠️ CET ÉVÈNEMENT N'EST PAS UNE ACTION UTILISATEUR : il n'appelle pas `markClientAction`.
 * S'il le faisait, chaque appel réseau deviendrait le parent des suivants et la timeline
 * prêterait à l'utilisateur des intentions qu'il n'a pas eues.
 */
export function callStart(chemin: string, methode: string): {
  seq: number; seqApi: number; fin: (statut: number | null) => void;
} {
  // DEUX RANGS, pas un (CCT-274). Une premiere version donnait le MEME a l'evenement client
  // et a l'appel serveur : l'audit a releve 36 rangs dupliques, et `parent=N` redevenait
  // ambigu — le defaut corrige en CCT-262, recree par une autre porte.
  //
  // L'appel client prend le premier, le traitement serveur le second, et ce dernier designe
  // le premier comme CAUSE. C'est la verite : ce qui a declenche le span serveur, c'est bien
  // cet appel-la, pas l'action utilisateur trois niveaux plus haut.
  const seq = nextSeq();
  const seqApi = nextSeq();
  const pile = pileJs();
  const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
  return {
    seq,
    seqApi,
    fin(statut: number | null) {
      try {
        const t1 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
        _buffer.push({
          type: 'fetch',
          action: methode,
          path: chemin,
          seq,
          ts: new Date().toISOString(),
          _sid: getSessionId(),
          meta: {
            pile,
            statut,
            // Latence PERÇUE, arrondie : la précision sub-milliseconde n'apporte rien et
            // gonfle le champ.
            latence_ms: Math.round((t1 - t0) * 10) / 10,
            cause: parentSeq(),
          },
        } as InsightEvent);
        _buffer = prune(_buffer, Date.now());
        persist();
        if (_buffer.length >= FLUSH_AT) flush();
      } catch {
        /* best-effort : l'observabilité ne casse jamais un appel */
      }
    },
  };
}

export function track(ev: InsightEvent): number | null {
  try {
    const seq = nextSeq();
    // Cette action devient le PARENT des appels qui vont suivre (CCT-253). Seules les actions
    // CLIENTES le font : un appel API ne cause pas les suivants, il les côtoie.
    markClientAction(seq);
    // SESSION D'ORIGINE FIGÉE ICI (CCT-261) — pas au moment de l'envoi.
    //
    // `flush` lisait `getSessionId()` à l'envoi. Un onglet fermé avec des événements en
    // attente les faisait repartir au chargement SUIVANT, donc sous une AUTRE session : la
    // session d'origine perdait sa fin — d'où des appels API désignant une action cliente
    // « absente de la session » — et la nouvelle recevait des rangs étrangers, en collision
    // avec les siens. Le blob d'une session close reste ouvert à l'ajout : rien n'obligeait
    // à les réattribuer.
    _buffer.push({ ...ev, seq, ts: new Date().toISOString(), _sid: getSessionId() });
    _buffer = prune(_buffer, Date.now());
    persist();
    if (_buffer.length >= FLUSH_AT) flush();
    return seq;
  } catch {
    /* best-effort */
    return null;
  }
}

/**
 * Rang de l'action cliente à l'origine d'un appel — en en ÉMETTANT une si aucune n'existe.
 *
 * ⚠️ LE MODÈLE N'ADMET PLUS D'APPEL SANS CAUSE (CCT-262). « Toutes les API ont un appel client
 * en parent. » Auparavant, un appel qui n'en trouvait pas répondait `bootstrap` (rien n'a
 * encore eu lieu) ou `none` (appel de fond) : deux façons de sortir du contrat par
 * déclaration, qui rendaient « zéro écart » atteignable sans rien corriger.
 *
 * Or ces appels ONT une cause — elle n'était simplement représentée par aucun évènement :
 *   * `boot`  : le démarrage lui-même, quand un appel part avant la première action ;
 *   * `wake`  : le réveil poussé par le service worker à l'arrivée d'une notification.
 *
 * On émet donc l'évènement client manquant, et l'appel le désigne. La timeline montre alors
 * ce qui s'est réellement passé, au lieu d'un appel orphelin.
 */
export function causeGarantie(reason: 'boot' | 'wake'): number | null {
  try {
    const already = parentSeq(false);
    if (already !== null) return already;
    return track({ type: reason, path: '/', action: 'start' });
  } catch {
    return null;
  }
}

/** Envoie le lot courant. Ne lève jamais ; conserve le surplus (> MAX_BATCH). */
export function flush(): void {
  try {
    if (_buffer.length === 0) return;
    // LE LOT N'EST PAS RETIRÉ ICI (CCT-253). Il l'était — `splice` — et l'échec d'envoi était
    // avalé : hors ligne, les actions disparaissaient sans laisser de trace. On COPIE, et on
    // ne retire qu'APRÈS confirmation du serveur (voir `envoyer`).
    //
    // UN ENVOI PAR SESSION D'ORIGINE (CCT-261) : le tampon peut contenir la fin d'une session
    // précédente, restée en attente à la fermeture de l'onglet. Chaque lot part sous SA session.
    const batch = _buffer.slice(0, MAX_BATCH);
    for (const [sid, evts] of grouperParSession(batch)) send(sid, evts);
  } catch {
    /* best-effort */
  }
}

function send(sid: string, batch: InsightEvent[]): void {
  try {
    // `_sid` est un marqueur LOCAL : il ne part pas au serveur, qui reçoit déjà `session_id`.
    const payload = JSON.stringify({
      session_id: sid,
      events: batch.map(({ _sid, ...e }: InsightEvent & { _sid?: string }) => e),
    });
    const url = endpoint();

    // CCT-237 — `sendBeacon` A ÉTÉ RETIRÉ, il ne pouvait pas fonctionner ici.
    //
    // Par SPÉCIFICATION, `navigator.sendBeacon()` émet toujours en mode de credentials
    // `include` — ce n'est pas configurable. Or l'API répond `Access-Control-Allow-Origin: *`
    // (CORS ouvert, sans credentials) : le navigateur INTERDIT cette combinaison et bloque
    // la requête au préflight. L'app et l'API étant sur des origines différentes en local
    // (:3000 vs :8080) COMME en dev (dev.cloudchrono.com vs dev.ws.cloudchrono.com), le
    // beacon n'a jamais rien envoyé.
    //
    // Et l'échec était SILENCIEUX : `sendBeacon()` renvoie `true` dès la mise en file, avant
    // tout échec réseau ou CORS. Le `if (!sent)` ne déclenchait donc JAMAIS le repli — les
    // événements disparaissaient sans laisser de trace.
    //
    // `keepalive: true` offre la MÊME garantie de survie au déchargement de la page (c'est
    // sa raison d'être), et `credentials: 'omit'` est de toute façon ce qu'il faut : de la
    // télémétrie n'a aucune raison de transporter des cookies vers une autre origine.
    try {
      void fetch(url, {
        method: 'POST',
        // `X-CC-Session` sur l'envoi LUI-MÊME (CCT-252) : sans lui, le serveur synthétisait
        // une session (`srv-…`) pour ce POST, et la liste des sessions se remplissait de
        // fantômes n'ayant jamais correspondu à aucun utilisateur — au point de noyer les
        // vraies. Le corps portait déjà le bon id, mais le SPAN de la requête, non.
        // Aucun coût de préflight : `Content-Type: application/json` en déclenche déjà un.
        // `X-CC-Session` porte la session DU LOT (`sid`), pas la session courante : sinon le
        // rattrapage d'une session close ferait porter au POST l'id de la session en cours.
        headers: { 'Content-Type': 'application/json', 'X-CC-Session': sid, 'X-CC-App': 'app' },
        body: payload,
        keepalive: true,
        credentials: 'omit',
      })
        .then((res) => {
          // SESSION RÉVOQUÉE (CCT-263) : elle a été supprimée côté serveur pendant que cet
          // onglet tournait. Continuer d'écrire ferait RENAÎTRE un blob amputé de son début,
          // dont les appels désigneraient des rangs disparus. On repart sur une session neuve
          // et on ré-estampille le lot : la suppression est respectée, la donnée en attente
          // n'est pas perdue, et elle atterrit dans une session cohérente.
          if (res && res.status === 409) {
            const neuve = newSessionId();
            _buffer = _buffer.map((e) => (sidDe(e) === sid ? { ...e, _sid: neuve } : e));
            persist();
            return;
          }
          // ACQUITTEMENT : on ne retire que ce que le serveur a accepté. Un 4xx/5xx laisse le
          // lot en attente, il repartira au flush suivant — c'est tout l'objet du correctif.
          if (!res || !res.ok) return;
          // La clé d'acquittement porte la SESSION : deux sessions numérotent leurs
          // événements à partir de 1, et un `seq` seul retirerait du tampon la ligne
          // homonyme d'une autre session, encore en attente.
          const sent = new Set(batch.map(ackKey));
          _buffer = _buffer.filter((e) => !sent.has(ackKey(e)));
          persist();
        })
        .catch(() => { /* hors ligne : le lot RESTE en attente, il repartira */ });
    } catch {
      /* best-effort : le lot reste en attente, jamais d'exception */
    }
  } catch {
    /* best-effort */
  }
}

// Dernière route annoncée — cf. `trackNavigation`.
let _lastRoute: string | null = null;

/** Émet un événement de navigation (appelé par NavigationContainer onReady/onStateChange).
 *
 * DÉDOUBLONNÉ (CCT-253). `onReady` et `onStateChange` se déclenchent tous deux au premier
 * rendu, et `onStateChange` refire sur des changements qui ne changent PAS de route (ouverture
 * d'un modal, paramètre mis à jour). Mesuré sur une session réelle : `/Login` deux fois,
 * `/Missions` deux fois, `/ChapterDetail` deux fois — la timeline racontait des navigations
 * qui n'ont pas eu lieu, et chaque doublon consommait un rang de séquence.
 *
 * On ignore donc une route IDENTIQUE à la précédente. Revenir sur un écran après en avoir
 * visité un autre reste bien deux événements : c'est la répétition immédiate qu'on coupe. */
export function trackNavigation(routeName?: string | null): void {
  if (!routeName) return;
  if (routeName === _lastRoute) return;
  _lastRoute = routeName;
  track({ type: 'navigate', path: `/${routeName}`, action: 'view' });
}

// --- Listeners globaux (aucun code par écran) --------------------------------

function installWebErrorListeners(): void {
  // RN Web uniquement : les API window/document n'existent pas en natif.
  if (Platform.OS !== 'web' || typeof window === 'undefined') return;
  try {
    window.addEventListener('error', (e: ErrorEvent) => {
      track({
        type: 'error',
        meta: {
          message: trunc(e.message),
          source: e.filename ? `${e.filename}:${e.lineno}:${e.colno}` : undefined,
          stack: trunc((e.error as Error | undefined)?.stack),
        },
      });
    });
    window.addEventListener('unhandledrejection', (e: PromiseRejectionEvent) => {
      const r = e.reason as { message?: string; stack?: string } | string | undefined;
      track({
        type: 'error',
        action: 'unhandledrejection',
        meta: {
          message: trunc(typeof r === 'string' ? r : r?.message),
          stack: trunc(typeof r === 'object' ? r?.stack : undefined),
        },
      });
    });
  } catch {
    /* ignore */
  }
}

// Natif : capture les erreurs JS non interceptées via ErrorUtils (appelle le handler d'origine).
function installNativeErrorHandler(): void {
  if (Platform.OS === 'web') return;
  try {
    const g = globalThis as unknown as {
      ErrorUtils?: {
        getGlobalHandler?: () => (err: unknown, isFatal?: boolean) => void;
        setGlobalHandler?: (h: (err: unknown, isFatal?: boolean) => void) => void;
      };
    };
    const eu = g.ErrorUtils;
    if (!eu || typeof eu.getGlobalHandler !== 'function' || typeof eu.setGlobalHandler !== 'function') return;
    const prev = eu.getGlobalHandler();
    eu.setGlobalHandler((err: unknown, isFatal?: boolean) => {
      try {
        const e = err as { message?: string; stack?: string } | undefined;
        track({
          type: 'error',
          action: isFatal ? 'fatal' : 'error',
          meta: { message: trunc(e?.message ?? String(err)), stack: trunc(e?.stack) },
        });
        flush(); // une erreur fatale peut précéder un crash : on pousse tout de suite.
      } catch {
        /* ignore */
      }
      if (typeof prev === 'function') prev(err, isFatal);
    });
  } catch {
    /* ignore */
  }
}

// Wrap léger de console.error : émet un event PUIS appelle l'original (comportement inchangé).
function wrapConsoleError(): void {
  try {
    const orig = console.error.bind(console);
    console.error = (...args: unknown[]) => {
      try {
        const msg = args
          .map((a) => (typeof a === 'string' ? a : a instanceof Error ? a.message : ''))
          .filter(Boolean)
          .join(' ');
        const stack = (args.find((a) => a instanceof Error) as Error | undefined)?.stack;
        track({ type: 'error', action: 'console', meta: { message: trunc(msg), stack: trunc(stack) } });
      } catch {
        /* ignore */
      }
      orig(...args);
    };
  } catch {
    /* ignore */
  }
}

function installFlushHooks(): void {
  try {
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') flush();
      });
      window.addEventListener('pagehide', flush);
      window.addEventListener('beforeunload', flush);
    } else {
      // Natif : pousser quand l'app passe en arrière-plan/inactif.
      AppState.addEventListener('change', (state) => {
        if (state === 'background' || state === 'inactive') flush();
      });
    }
  } catch {
    /* ignore */
  }
}

/**
 * Initialise le beacon : session, timer de flush, hooks de bascule, listeners
 * d'erreurs (web + natif) et wrap console.error. Idempotent. À appeler UNE fois
 * au bootstrap racine.
 */
export function initInsights(): void {
  if (_started) return;
  _started = true;

  try {
    getSessionId();
  } catch {
    /* ignore */
  }
  // Persistance native best-effort (no-op sur web).
  void hydrateSessionId();

  // RATTRAPAGE HORS LIGNE (CCT-253) : les événements restés en attente au chargement
  // précédent — coupure réseau, onglet fermé pendant un envoi — repartent maintenant.
  //
  // ⚠️ CORRIGÉ (CCT-261). Ils repartent sous LEUR session d'origine, pas sous la session
  // courante. Le raisonnement inverse était écrit ici — « la session d'origine n'existe
  // plus » — et il était faux : un blob de session reste ouvert à l'ajout, rien n'obligeait
  // à réattribuer. Le coût de cette erreur se lisait dans la timeline : la session close
  // perdait sa fin, ses derniers appels API désignaient une action cliente « absente de la
  // session », et la session courante héritait de rangs étrangers.
  restore();

  try {
    _timer = setInterval(flush, FLUSH_INTERVAL_MS);
  } catch {
    /* ignore */
  }

  installFlushHooks();
  installWebErrorListeners();
  installNativeErrorHandler();
  wrapConsoleError();

  // DÉMARRAGE = une action cliente, et la RACINE de la session (CCT-259).
  //
  // Les appels lancés au montage — catalogue, profil, notifications — partaient avant qu'aucune
  // action n'ait été enregistrée : ils n'avaient donc aucune cause à désigner et arrivaient
  // marqués `bootstrap`. C'est exact mais incomplet : ce qui les a causés, c'est bien
  // l'ouverture de l'application. En l'annonçant ICI — au chargement du module, avant que React
  // ne monte quoi que ce soit — ces appels se rattachent à un événement RÉEL au lieu de flotter.
  //
  // Émis en dernier dans `initInsights` : après `restore()`, pour que les événements rattrapés
  // du chargement précédent gardent leur propre rang, et après l'installation des listeners,
  // pour qu'une erreur au démarrage soit déjà captée.
  track({ type: 'boot', path: '/', action: 'start' });
}
